import React, { useContext, useEffect, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { merge } from 'lodash';

import WDT_CLASSROOM_PROGRESS_BY_CHARACTERS_QUERY from 'graph/reports/wdt-classroom-progress-by-characters-report-query';
import useReportQueryParams from '../../../../hooks/use-report-query-params';
import BarChart from 'components/@common/Charts/BarChart';
import CharacterOrderAndFilter from '../../common/CharacterOrderAndFilter';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import { ModuleType } from '../../../../types';
import { getQueryVariables } from '../../../../utils/report-param-utils';
import { PrintViewContext } from '../../../../providers/PrintViewProvider';
import {
  WdtCharacterGroup,
  WdtCharacterOrder,
  WdtClassroomProgressByCharactersData
} from 'types/schema-generated-types';
import {
  BAR_CHART_COMMON_OPTIONS,
  MODULE_LEVELS_COLOR_MAP
} from '../../../../utils/report-wdt-utils';

interface ProgressForCharactersChartViewProps {
  module: ModuleType;
}

const ProgressForCharactersChartView: React.FC<ProgressForCharactersChartViewProps> = ({
  module
}) => {
  const { fullChartWidth } = useContext(PrintViewContext);
  const [{ dateRange, entityType, entityId }] = useReportQueryParams();
  const [fetchReport, { data, loading }] = useLazyQuery(WDT_CLASSROOM_PROGRESS_BY_CHARACTERS_QUERY);
  const { t } = useTranslation('Reports');
  const [characterOrder, setCharacterOrder] = useState(WdtCharacterOrder.natural);
  const [characterGroup, setCharacterGroup] = useState<number>();

  const reportData: WdtClassroomProgressByCharactersData[] =
    data?.wdtClassroomProgressByCharactersReport?.characterProgress ?? [];

  const characterGroups: WdtCharacterGroup[] =
    data?.wdtClassroomProgressByCharactersReport?.characterGroups;

  const filterCharacterGroup = (item: WdtClassroomProgressByCharactersData): boolean =>
    !characterGroup || item.character?.characterGroupIds?.includes(characterGroup);

  const filteredData = reportData.filter(filterCharacterGroup);

  const characters = [...new Set(filteredData.map(item => item?.character?.character))];

  const getLevelSeries = (level: number): number[] =>
    filteredData
      .flatMap(item => item.characterLevelsProgress)
      .filter(item => item.level === level)
      .map(item => item.studentCompletedCount);

  const series = [
    { name: '1 Star', data: getLevelSeries(1) },
    { name: '2 Stars', data: getLevelSeries(2) },
    { name: '3 Stars', data: getLevelSeries(3) }
  ];

  const options = merge({}, BAR_CHART_COMMON_OPTIONS, {
    colors: MODULE_LEVELS_COLOR_MAP[module],
    yaxis: { title: { text: 'No. of Students' } },
    xaxis: { categories: characters },
    plotOptions: { bar: { columnWidth: `${series[0].data.length * 3}%` } },
    stroke: { show: true, colors: ['transparent'], width: 2 }
  });

  useEffect(() => {
    fetchReport({
      variables: merge(getQueryVariables({ entityId, entityType, dateRange }), {
        where: {
          module,
          characterOrder
        }
      })
    });
  }, [fetchReport, entityId, entityType, dateRange, module, characterOrder]);

  const cardTitle = (
    <Box
      display="grid"
      gridAutoFlow="column"
      justifyContent="flex-start"
      alignItems="center"
      gridGap="1rem"
      mb={2}
    >
      <Typography variant="h4">{t(`Progress for ${t(module, { ns: 'ModuleType' })}`)}</Typography>
      {module !== ModuleType.NUMBERS && (
        <CharacterOrderAndFilter
          characterGroups={characterGroups}
          order={characterOrder}
          groupId={characterGroup}
          setOrder={setCharacterOrder}
          setGroupId={setCharacterGroup}
        />
      )}
    </Box>
  );

  return (
    <ReportCard title={cardTitle} loading={loading} reportData={reportData}>
      <BarChart height={250} width={fullChartWidth} series={series} options={options} />
    </ReportCard>
  );
};

export default ProgressForCharactersChartView;
